import React, { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  Checkbox,
  Input,
  Upload,
  Button,
  Space,
  Radio,
  Select,
  message,
  Alert,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  UserOutlined,
  MailOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { ChecklistItem, AssignablePerson } from '../../types';
import { useLookups } from '../../context/LookupsContext';
import api from '../../services/api';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

interface StatusChangeModalProps {
  visible: boolean;
  item: ChecklistItem | null;
  targetStatus: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  visible,
  item,
  targetStatus,
  onSuccess,
  onCancel,
}) => {
  const { getStatusName } = useLookups();
  const [confirmed, setConfirmed] = useState(false);
  const [response, setResponse] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);

  // Assignee selection state (for 'requested' status)
  const [assigneeType, setAssigneeType] = useState<'team' | 'external'>('team');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [externalEmail, setExternalEmail] = useState('');
  const [assignablePeople, setAssignablePeople] = useState<AssignablePerson[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Fetch assignable people when modal opens for 'requested' status
  useEffect(() => {
    const fetchAssignablePeople = async () => {
      if (!visible || targetStatus !== 'requested' || !item?.checklist?.project?.id) return;

      setLoadingPeople(true);
      try {
        // Get receiver teams from the template item
        const receiverTeams = item.templateItem?.receiverTeams || [];
        const projectId = item.checklist.project.id;
        const people = await api.getAssignablePeople(projectId, receiverTeams);
        setAssignablePeople(people);
      } catch (error) {
        console.error('Failed to fetch assignable people:', error);
        message.error('Failed to load team members');
      } finally {
        setLoadingPeople(false);
      }
    };

    fetchAssignablePeople();
  }, [visible, targetStatus, item]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setConfirmed(false);
      setResponse('');
      setFileList([]);
      setAssigneeType('team');
      setSelectedAssignee('');
      setExternalEmail('');
    }
  }, [visible]);

  if (!item) return null;

  const itemType = item.templateItem?.itemType || 'task';
  const itemText = item.templateItem?.itemText || '';
  const proofType = item.templateItem?.proofType;

  // Determine what input is required based on item type and target status
  const isRequesting = targetStatus === 'requested';
  const isResolving = targetStatus === 'resolved';
  const requiresTextResponse = isResolving && (itemType === 'question' || itemType === 'decision_question');
  // File upload required for document items OR any item with a proof type (letter, report, contract, etc.)
  const requiresFileUpload = isResolving && (itemType === 'document' || itemType.includes('document') || !!proofType);
  const requiresInput = requiresTextResponse || requiresFileUpload;

  // Get the selected assignee email
  const getAssigneeEmail = (): string => {
    if (assigneeType === 'team') {
      return selectedAssignee;
    }
    return externalEmail.trim();
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validation
  const isTextValid = !requiresTextResponse || response.trim().length > 0;
  const isFileValid = !requiresFileUpload || fileList.length > 0;
  const isAssigneeValid = !isRequesting || (
    (assigneeType === 'team' && selectedAssignee) ||
    (assigneeType === 'external' && isValidEmail(externalEmail))
  );
  const canSubmit = confirmed && isTextValid && isFileValid && isAssigneeValid;

  const getModalTitle = () => {
    switch (targetStatus) {
      case 'requested':
        return 'Request Item';
      case 'received':
        return 'Mark as Received';
      case 'resolved':
        return 'Resolve Item';
      case 'approved':
        return 'Approve Item';
      default:
        return `Update to ${getStatusName(targetStatus)}`;
    }
  };

  const getConfirmationText = () => {
    switch (targetStatus) {
      case 'requested':
        return 'I confirm that I am requesting this item from the responsible team.';
      case 'received':
        return 'I confirm that I have received this request and will work on it.';
      case 'resolved':
        return 'I confirm that I have completed this item and take responsibility for its accuracy.';
      case 'approved':
        return 'I confirm that I have reviewed this item and approve its completion.';
      default:
        return 'I confirm this status change.';
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || !item) return;

    setLoading(true);
    try {
      // If resolving and requires file upload, upload files first
      if (requiresFileUpload && fileList.length > 0) {
        for (const file of fileList) {
          if (file.originFileObj) {
            await api.uploadAttachment(item.id, file.originFileObj);
          }
        }
      }

      // Update status (with response if applicable)
      if (targetStatus === 'resolved' && response.trim()) {
        await api.resolveItem(item.id, { response: response.trim() });
      } else if (targetStatus === 'requested') {
        // Include the assignee email when requesting
        const assigneeEmail = getAssigneeEmail();
        await api.updateItemStatus(item.id, targetStatus, assigneeEmail);
      } else {
        await api.updateItemStatus(item.id, targetStatus);
      }

      message.success(`Item ${getStatusName(targetStatus).toLowerCase()}`);
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleFileRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const beforeUpload = (file: File) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB');
      return false;
    }
    return false; // Prevent auto-upload, we'll handle it on submit
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!canSubmit}
        >
          Confirm
        </Button>,
      ]}
      width={500}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Item description */}
        <div>
          <Text type="secondary">Item:</Text>
          <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
            {itemText}
          </Paragraph>
        </div>

        {/* Assignee selection for requesting */}
        {isRequesting && (
          <div>
            <Text strong>
              Assign To <Text type="danger">*</Text>
            </Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group
                value={assigneeType}
                onChange={(e) => {
                  setAssigneeType(e.target.value);
                  setSelectedAssignee('');
                  setExternalEmail('');
                }}
                style={{ marginBottom: 12 }}
              >
                <Radio value="team">
                  <Space>
                    <UserOutlined />
                    Team Member
                  </Space>
                </Radio>
                <Radio value="external">
                  <Space>
                    <MailOutlined />
                    External Email
                  </Space>
                </Radio>
              </Radio.Group>

              {assigneeType === 'team' && (
                <div>
                  {loadingPeople ? (
                    <div style={{ textAlign: 'center', padding: 16 }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8 }}>Loading team members...</Text>
                    </div>
                  ) : assignablePeople.length === 0 ? (
                    <Alert
                      type="warning"
                      message="No team members found"
                      description="No team members are assigned to the receiver teams for this item. Use 'External Email' to assign to someone outside the project."
                      showIcon
                    />
                  ) : (
                    <Select
                      placeholder="Select a team member"
                      value={selectedAssignee || undefined}
                      onChange={setSelectedAssignee}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="children"
                    >
                      {assignablePeople.map((person) => (
                        <Select.Option key={person.email} value={person.email}>
                          {person.name || person.email}
                          {person.name && (
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                              ({person.email})
                            </Text>
                          )}
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
                            [{person.teams.join(', ')}]
                          </Text>
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
              )}

              {assigneeType === 'external' && (
                <div>
                  <Input
                    placeholder="Enter email address"
                    value={externalEmail}
                    onChange={(e) => setExternalEmail(e.target.value)}
                    prefix={<MailOutlined />}
                  />
                  {externalEmail && !isValidEmail(externalEmail) && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      Please enter a valid email address
                    </Text>
                  )}
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    The person will be notified and can be added to the project later.
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Text response for questions */}
        {requiresTextResponse && (
          <div>
            <Text strong>
              Response <Text type="danger">*</Text>
            </Text>
            <TextArea
              placeholder="Enter your response to this question..."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              style={{ marginTop: 8 }}
            />
            {!isTextValid && response.length === 0 && (
              <Text type="danger" style={{ fontSize: 12 }}>
                A response is required for this item
              </Text>
            )}
          </div>
        )}

        {/* File upload for documents or items with proof type */}
        {requiresFileUpload && (
          <div>
            <Text strong>
              Upload {proofType ? proofType.replace(/_/g, ' ') : 'Document'} <Text type="danger">*</Text>
            </Text>
            <Upload.Dragger
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              onRemove={handleFileRemove}
              beforeUpload={beforeUpload}
              multiple
              style={{ marginTop: 8 }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to upload
              </p>
              <p className="ant-upload-hint">
                Maximum file size: 10MB
              </p>
            </Upload.Dragger>
            {!isFileValid && (
              <Text type="danger" style={{ fontSize: 12 }}>
                At least one file is required for this item
              </Text>
            )}
          </div>
        )}

        {/* Info for non-input items */}
        {!requiresInput && isResolving && (
          <Alert
            type="info"
            message="This is a task item. No additional input is required."
            showIcon
          />
        )}

        {/* Confirmation checkbox */}
        <div style={{ marginTop: 8 }}>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          >
            <Text>{getConfirmationText()}</Text>
          </Checkbox>
        </div>
      </Space>
    </Modal>
  );
};

export default StatusChangeModal;
